/**
 * One slide's decode of a TIFF or a HEIC, for the slide that is on screen.
 *
 * Per slide and not per set, which is the whole reason this is a hook and not
 * a step in the loader: the viewer mounts three slides at a time, so opening an
 * entry with twelve scans decodes one rather than twelve. `spotlight.decode.ts`
 * owns the decoding itself; this owns the lifecycle — the fetch that may land
 * after the slide is gone, and the object URL that has to be revoked or it
 * holds a decoded bitmap for the life of the tab.
 */

import { useEffect, useState } from "react";

import { decodeToImage } from "./spotlight.decode.ts";

/**
 * Where one decode has got to.
 *
 * A union rather than a URL and a boolean, so "still working" cannot carry a
 * URL and "failed" cannot be mistaken for an empty image.
 */
export type Decoded =
  | { kind: "decoding" }
  | { kind: "shown"; url: string }
  | { kind: "failed" };

/**
 * A TIFF or a HEIC, fetched and decoded for one slide.
 *
 * Per SLIDE rather than per set, and that is the whole point of doing this in a
 * hook: the viewer mounts the slide either side of the one being read and no
 * more, so opening an entry with twelve scans decodes the one that is on screen
 * instead of all twelve. The set is still signed in one round trip — signing is
 * cheap and decoding is not.
 *
 * The object URL is revoked on cleanup. A blob left unrevoked holds its decoded
 * bitmap in memory for the life of the tab, which for a page of A3 at 600dpi is
 * tens of megabytes per document somebody glanced at.
 */
export function useDecodedImage(url: string, contentType: string): Decoded {
  const [state, setState] = useState<Decoded>({ kind: "decoding" });

  useEffect(() => {
    // The `active` guard every controller in this app uses, and here it also
    // decides whether the object URL is ours to keep: a decode that lands after
    // the slide is gone revokes its own result rather than leaking it.
    let active = true;
    let decoded: string | null = null;

    decodeToImage(url, contentType)
      .then((objectUrl) => {
        decoded = objectUrl;
        if (active) setState({ kind: "shown", url: objectUrl });
        else URL.revokeObjectURL(objectUrl);
      })
      .catch(() => {
        // The reason is not carried through. There are three of them — a fetch,
        // a decoder, a canvas — and none is something the reader can act on
        // differently: the download link is the answer to all three.
        if (active) setState({ kind: "failed" });
      });

    return () => {
      active = false;
      if (decoded) URL.revokeObjectURL(decoded);
    };
  }, [url, contentType]);

  return state;
}
